import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import {
  ArrowLeft,
  Car,
  Calendar,
  Hash,
  User,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  ExternalLink,
  Pencil,
  UserPlus,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { cn } from '../utils/cn';
import AddVehicleModal from '../components/AddVehicleModal';

const approvalConfig = {
  pending: { label: 'Pending admin approval', color: 'warning', icon: Clock },
  approved: { label: 'Approved', color: 'success', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'danger', icon: XCircle },
};

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState('');

  const { data: vehicleData, isLoading } = useQuery({
    queryKey: ['fleet-vehicle', id],
    queryFn: () => fleetService.getVehicle(id).then((res) => res.data),
  });

  const vehiclePayload = vehicleData?.data;
  const approvalStatus = vehiclePayload?.approval_status || 'pending';

  const { data: driversListRes } = useQuery({
    queryKey: ['fleet-drivers-for-assign', id],
    queryFn: () =>
      fleetService.getDrivers({ status: 'approved', limit: 200, page: 1 }).then((res) => res.data),
    enabled: approvalStatus === 'approved' && !!id && vehiclePayload?.status !== 'deactivated',
  });

  const assignableDrivers = (driversListRes?.data ?? []).filter(
    (d) => d.approval_status === 'approved' && d.status === 'active'
  );

  const assignMutation = useMutation({
    mutationFn: (driverId) => fleetService.assignVehicleToDriver(id, driverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicle', id] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers-for-assign'] });
      showSuccess('Driver assigned to this vehicle.');
    },
    onError: (err) => showError(err?.response?.data?.message || err.message || 'Could not assign driver'),
  });

  const unassignMutation = useMutation({
    mutationFn: () => fleetService.unassignVehicle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicle', id] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers-for-assign'] });
      showSuccess('Vehicle unassigned.');
      setSelectedDriverId('');
    },
    onError: (err) => showError(err?.response?.data?.message || err.message || 'Could not unassign'),
  });

  useEffect(() => {
    const drvId = vehiclePayload?.driver_id;
    if (drvId) setSelectedDriverId(drvId);
    else if (!isLoading) setSelectedDriverId('');
  }, [vehiclePayload?.driver_id, isLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <div className="text-text-secondary dark:text-text-secondary-dark">Loading vehicle details...</div>
        </div>
      </div>
    );
  }

  const vehicle = vehiclePayload || {};
  const isDeactivated = vehicle.status === 'deactivated';
  const isAssigned = Boolean(vehicle.driver_id);
  const appr = approvalConfig[approvalStatus] || approvalConfig.pending;
  const ApprIcon = appr.icon;
  const documents = Array.isArray(vehicle.documents) ? vehicle.documents : [];

  return (
    <div className="space-y-4 lg:space-y-6">
      <button
        onClick={() => navigate('/vehicles')}
        className="flex items-center gap-2 text-text-secondary dark:text-text-secondary-dark hover:text-text dark:hover:text-text-dark transition-colors group"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Back to Vehicles</span>
      </button>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {!isDeactivated && (
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-border dark:border-border-dark rounded-lg font-medium hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark"
          >
            <Pencil className="w-4 h-4" />
            Edit vehicle
          </button>
        )}
      </div>

      {isDeactivated && (
        <div className="rounded-xl border border-warning/40 bg-warning-light dark:bg-warning-dark/20 px-4 py-3 text-sm text-text dark:text-text-dark">
          This vehicle is <span className="font-semibold">deactivated</span>. It cannot be edited or assigned. Contact an
          administrator to reactivate it.
        </div>
      )}

      <AddVehicleModal isOpen={editOpen} onClose={() => setEditOpen(false)} mode="edit" vehicleId={id} />

      <div className="bg-gradient-to-br from-info/10 to-info/5 dark:from-info-dark/10 dark:to-info-dark/5 rounded-2xl p-6 lg:p-8 border border-info/20 dark:border-info-dark/20 shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-info to-info-dark rounded-2xl flex items-center justify-center text-white shadow-xl">
            <Car className="w-10 h-10 lg:w-12 lg:h-12" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-2">
              {vehicle.model && vehicle.model !== '—' ? `${vehicle.make} ${vehicle.model}` : vehicle.make} ({vehicle.year})
            </h1>
            <p className="text-text-secondary dark:text-text-secondary-dark mb-4">
              {vehicle.color} • {vehicle.license_plate || vehicle.licensePlate}
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <span
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold',
                  appr.color === 'success'
                    ? 'bg-success-light dark:bg-success-dark text-success'
                    : appr.color === 'warning'
                    ? 'bg-warning-light dark:bg-warning-dark text-warning'
                    : 'bg-danger-light dark:bg-danger-dark text-danger'
                )}
              >
                <ApprIcon className="w-4 h-4" />
                {appr.label}
              </span>
              {isDeactivated ? (
                <span className="inline-flex items-center gap-1 text-danger text-sm font-medium">
                  <XCircle className="w-4 h-4" /> Deactivated
                </span>
              ) : vehicle.is_active && approvalStatus === 'approved' ? (
                <span className="inline-flex items-center gap-1 text-success text-sm font-medium">
                  <CheckCircle className="w-4 h-4" /> Active for trips
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-text-secondary text-sm">
                  <XCircle className="w-4 h-4" /> Not active until approved
                </span>
              )}
            </div>
            {approvalStatus === 'rejected' && vehicle.rejection_reason && (
              <p className="mt-3 text-sm text-danger">Reason: {vehicle.rejection_reason}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-6 border border-border dark:border-border-dark shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <Car className="w-5 h-5 text-primary" />
            Vehicle information
          </h2>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
              <p className="text-xs text-text-secondary mb-1">Vehicle manufacturer</p>
              <p className="font-medium">{vehicle.make}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark flex items-start gap-3">
              <Calendar className="w-5 h-5 text-text-tertiary mt-0.5" />
              <div>
                <p className="text-xs text-text-secondary mb-1">Year</p>
                <p className="font-medium">{vehicle.year}</p>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
              <p className="text-xs text-text-secondary mb-1">Licence plate</p>
              <p className="font-mono font-medium">{vehicle.license_plate || vehicle.licensePlate}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
              <p className="text-xs text-text-secondary mb-1">Vehicle color</p>
              <p className="font-medium">{vehicle.color || '—'}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
              <p className="text-xs text-text-secondary mb-1">Transport licence number</p>
              <p className="font-mono font-medium">{vehicle.transport_licence_number || vehicle.transportLicenceNumber || '—'}</p>
            </div>
            {(vehicle.vehicle_comment || vehicle.vehicleComment) && (
              <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
                <p className="text-xs text-text-secondary mb-1">Comment</p>
                <p className="text-sm font-medium">{vehicle.vehicle_comment || vehicle.vehicleComment}</p>
              </div>
            )}
            <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
              <p className="text-xs text-text-secondary mb-1">Insurance details</p>
              <p className="text-sm font-medium">{vehicle.insurance_details || '—'}</p>
            </div>
          </div>
        </div>

        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-6 border border-border dark:border-border-dark shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Assigned driver
          </h2>
          {approvalStatus !== 'approved' && (
            <p className="text-sm text-warning mb-4">
              Only approved vehicles can be assigned to a driver.
            </p>
          )}
          {approvalStatus === 'approved' && !isDeactivated && (
            <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-4">
              You can assign a driver here, or drivers can pick an available approved vehicle in the app. Only one
              vehicle is active per driver at a time.
            </p>
          )}
          {isDeactivated && (
            <p className="text-sm text-warning mb-4">
              This vehicle is deactivated. An administrator must reactivate it before you can assign drivers again.
            </p>
          )}
          {isAssigned ? (
            <div className="space-y-3">
              {vehicle.driver ? (
                <>
                  <p className="font-semibold">
                    {vehicle.driver.first_name} {vehicle.driver.last_name}
                  </p>
                  <p className="text-sm text-text-secondary">{vehicle.driver.email}</p>
                  <Link
                    to={`/drivers/${vehicle.driver.id}`}
                    className="inline-flex text-sm text-primary font-medium hover:underline"
                  >
                    View driver profile
                  </Link>
                </>
              ) : (
                <Link
                  to={`/drivers/${vehicle.driver_id}`}
                  className="inline-flex text-sm text-primary font-medium hover:underline"
                >
                  View assigned driver
                </Link>
              )}
            </div>
          ) : (
            <p className="text-text-secondary text-sm">No driver assigned.</p>
          )}
          {approvalStatus === 'approved' && !isDeactivated && (
            <div className="mt-6 space-y-3 pt-4 border-t border-border dark:border-border-dark">
              <label htmlFor="assign-driver" className="block text-sm font-medium text-text dark:text-text-dark">
                {isAssigned ? 'Assign to a different driver' : 'Assign driver'}
              </label>
              <select
                id="assign-driver"
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text dark:text-text-dark"
              >
                <option value="">Select an approved driver…</option>
                {assignableDrivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.first_name} {d.last_name} — {d.email}
                  </option>
                ))}
              </select>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!selectedDriverId || assignMutation.isPending}
                  onClick={() => selectedDriverId && assignMutation.mutate(selectedDriverId)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 disabled:pointer-events-none"
                >
                  <UserPlus className="w-4 h-4" />
                  {assignMutation.isPending ? 'Saving…' : isAssigned ? 'Apply assignment' : 'Assign'}
                </button>
                {isAssigned && (
                  <button
                    type="button"
                    disabled={unassignMutation.isPending}
                    onClick={() => {
                      if (window.confirm('Unassign this vehicle from the driver?')) unassignMutation.mutate();
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark disabled:opacity-50"
                  >
                    {unassignMutation.isPending ? 'Removing…' : 'Unassign'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-surface dark:bg-surface-dark rounded-2xl p-6 border border-border dark:border-border-dark shadow-lg">
        <h2 className="text-xl font-bold text-text dark:text-text-dark mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Documents
        </h2>
        {documents.length === 0 ? (
          <p className="text-sm text-text-secondary">No documents on file.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {documents.map((doc) => (
              <div key={doc.id} className="p-4 rounded-xl bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark">
                <p className="font-medium">{doc.display_name || doc.document_type}</p>
                {doc.url ? (
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary mt-2">
                    <ExternalLink className="w-4 h-4" /> View
                  </a>
                ) : (
                  <p className="text-xs text-text-tertiary mt-2">Link unavailable</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
